import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { selectLoggedInUserState } from '@store';
import {
  bookingsServices,
  usersServices,
  b2bServices,
  communicationServices,
  boardsServices,
} from '@services';
import { asyncForEach, showError, showSuccess } from '@utils';
import { QUERY_KEYS } from 'constants/queryKeys';
import {
  BOOKINGS_STAGES,
  BOOKINGS_STAGES_ORDER,
  BOOKING_STATUS,
} from 'components/bookings/constants';
import { useAssignees, useInfinitePagination } from 'hooks/common';
import {
  ENTITY_TYPES_COMMENTS,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';
import { useHistoryItemsChangelog } from 'hooks/historyItems';
import { useMailBoxesOperations } from 'hooks/mailBoxes';
import { DEFAULT_MAILBOX } from 'hooks/communication/constants';

import {
  handleBookingData,
  normalizeUserData,
  normalizeVehicleData,
  getUpdateBookingPayload,
  handleOrders,
  handleBookingComments,
  handleChangeLog,
  handleSMS,
  handleInfiniteQueryBookingData,
  handleEmails,
  handleCustomerTypesEnums,
  handleLostReasonsEnums,
  handleBookingLabels,
  handleLinkedTasks,
} from './utils';
import { useBookingErrors } from './useBookingErrors';
import { BOOKING_HISTORY_CHANGES_DEFAULT_PAGE_SIZE } from './constants';

export const useBookingOperations = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const {
    assignees,
    isLoading: isAssigneesLoading,
    isFetched: isAssigneesFetched,
    getAssigneeName,
  } = useAssignees();
  const { mailBoxes } = useMailBoxesOperations();
  const loggedInUser = useSelector(selectLoggedInUserState);
  const bookingId = router.query?.bookingId;

  const { isSystemActor, handleChangelogItemFieldName } =
    useHistoryItemsChangelog();

  const isHistoryTabComment = router.query?.historyTab === 'comment';
  const isHistoryTabChangelog = router.query?.historyTab === 'changelog';
  const isHistoryTabSMS = router.query?.historyTab === 'sms';
  const isHistoryTabAll =
    !router.query?.historyTab || router.query?.historyTab === 'all';
  const isHistoryTabEmails = router.query?.historyTab === 'email';

  const selectedMailbox =
    mailBoxes?.find((mailbox) => mailbox?.address === DEFAULT_MAILBOX) ||
    mailBoxes?.[0] ||
    null;
  const agent = assignees?.find((item) => item.email === loggedInUser?.email);

  const ordersResponse = useQuery(QUERY_KEYS.GET_B2B_ORDERS_KEY, () =>
    b2bServices.getB2BOrders({
      page: 1,
      pageSize: 3000,
      orderBy: 'createdAt',
      order: 'desc',
    })
  );

  const labelsResponse = useQuery([QUERY_KEYS.GET_BOOKING_LABELS_KEY], () =>
    bookingsServices.getBookingLabels({
      params: { page: 1, pageSize: 200 },
      payload: {
        type: 0,
        field: null,
        operator: null,
        value: [
          {
            field: 'is_disabled',
            operator: FILTER_OPERATOR_ENUMS['='],
            type: FILTER_RULE_ENUMS.ComparableRuleType,
            value: false,
          },
        ],
      },
    })
  );

  const bookingLabelsOptions = useMemo(
    () => handleBookingLabels(labelsResponse?.data?.data || []),
    [labelsResponse?.data?.data]
  );

  const boardsEnumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_TASK_STATUSES_KEY],
    () => boardsServices.getBoardsEnum()
  );

  const linkedTasksResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_LINKED_TASKS_KEY, bookingId],
    () =>
      bookingsServices.getBookingLinkedTasks({
        params: { page: 1, pageSize: 200, orderBy: 'createdAt', order: 'desc' },
        payload: {
          type: 0,
          value: [
            {
              field: 'customFields.bookingId',
              operator: FILTER_OPERATOR_ENUMS['='],
              type: FILTER_RULE_ENUMS.ComparableRuleType,
              value: bookingId,
            },
          ],
        },
      }),
    {
      enabled: !!bookingId,
    }
  );

  const linkedTasks = useMemo(
    () =>
      handleLinkedTasks(
        linkedTasksResponse?.data?.data || [],
        boardsEnumsResponse?.data?.data?.taskStatuses || []
      ),
    [
      linkedTasksResponse?.data?.data,
      boardsEnumsResponse?.data?.data?.taskStatuses,
    ]
  );

  const bookingResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_KEY, bookingId],
    () => bookingsServices.getBookingById(bookingId),
    {
      enabled: !!bookingId && isAssigneesFetched,
    }
  );

  const userResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_USER_KEY, bookingId],
    () => usersServices.getUser(bookingResponse.data.data?.userId),
    {
      enabled:
        !!bookingId &&
        bookingResponse.isFetched &&
        !!bookingResponse?.data?.data?.userId,
    }
  );

  const enumsResponse = useQuery([QUERY_KEYS.GET_BOOKINGS_ENUMS_KEY], () =>
    bookingsServices.getBookingsEnums()
  );

  const lostReasonOptions = useMemo(
    () => handleLostReasonsEnums(enumsResponse?.data?.data || {}),
    [enumsResponse?.data?.data]
  );

  const customerTypesOptions = useMemo(
    () => handleCustomerTypesEnums(enumsResponse?.data?.data || {}),
    [enumsResponse?.data?.data]
  );

  const commentsResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_COMMENTS_KEY, bookingId],
    () => bookingsServices.getBookingComments(bookingId),
    {
      enabled: !!bookingId && isHistoryTabComment,
    }
  );

  const changelogResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_CHANGELOG_KEY, bookingId],
    () => bookingsServices.getBookingChangelog(bookingId),
    {
      enabled: !!bookingId && isHistoryTabChangelog,
    }
  );

  const smsResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_SMS_KEY, bookingId],
    () =>
      bookingsServices.getBookingSMS({
        params: { page: 1, pageSize: 500, order: 'desc', orderBy: 'createdAt' },
        payload: {
          type: 0,
          value: [
            {
              field: 'entity_id',
              operator: FILTER_OPERATOR_ENUMS['='],
              type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
              value: [bookingId],
            },
          ],
        },
      }),
    {
      enabled: !!bookingId && isHistoryTabSMS,
    }
  );

  const entityID = bookingResponse.data?.data?.entityID;

  const emailsResponse = useQuery(
    [QUERY_KEYS.GET_BOOKING_EMAILS_KEY, bookingId],
    () =>
      bookingsServices.getBookingEmails({
        bookingId: entityID,
        params: { page: 1, pageSize: 500, order: 'desc', orderBy: 'createdAt' },
      }),
    {
      enabled:
        !!bookingId &&
        isHistoryTabEmails &&
        bookingResponse.isFetched &&
        !!bookingResponse.data?.data?.entityID,
    }
  );

  const booking = useMemo(
    () =>
      handleBookingData({
        booking: bookingResponse?.data?.data || [],
        assignees,
        bookingLabelsOptions: bookingLabelsOptions || [],
      }),
    [bookingResponse?.data?.data, assignees, bookingLabelsOptions]
  );

  const isBookingLost = useMemo(
    () => booking?.status === BOOKING_STATUS.lost,
    [booking]
  );

  const updateBooking = async ({ values, documents }) => {
    if (isBookingLost) return;

    try {
      const payload = getUpdateBookingPayload(values);
      await bookingsServices.updateBooking(bookingId, payload);

      const agreements = values?.agreements || [];
      await asyncForEach(agreements, async (agreement) => {
        await bookingsServices.addGuarantorAgreement(bookingId, agreement);
      });

      const documentsToUpLoad = documents?.filter((doc) => doc?.localFile);

      await asyncForEach(documentsToUpLoad, async (doc) => {
        await bookingsServices.addDocument(bookingId, doc?.file);
      });
    } catch (error) {
      showError(error);
    }
  };

  const vehicle = normalizeVehicleData(bookingResponse?.data?.data || {});

  const user = normalizeUserData(
    userResponse?.data?.data || {},
    bookingResponse?.data?.data
  );
  const orders = handleOrders(ordersResponse?.data?.data || []);

  const stages = BOOKINGS_STAGES_ORDER.reduce((acc, cur) => {
    acc.push({ text: BOOKINGS_STAGES[cur].label, value: Number(cur) });

    return acc;
  }, []);

  const comments = useMemo(() => {
    return handleBookingComments({
      data: commentsResponse?.data?.data || [],
      loggedInUser,
      getAssigneeName,
    });
  }, [commentsResponse?.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCommentMutation = useMutation(bookingsServices.addBookingComment);

  const deleteCommentMutation = useMutation(
    bookingsServices.deleteBookingComment
  );

  const emailMutation = useMutation(communicationServices.replyEmailV2);

  const createBookingLabelMutation = useMutation((labelName) =>
    bookingsServices.createBookingLabel(labelName)
  );

  const patchBookingLabelMutation = useMutation(({ labelId, payload }) =>
    bookingsServices.patchBookingLabel(labelId, payload)
  );

  const bookingHistoryParams = useMemo(
    () => ({
      bookingId,
      pageSize: BOOKING_HISTORY_CHANGES_DEFAULT_PAGE_SIZE,
    }),
    [bookingId]
  );

  const bookingHistoryQueryInfiniteQuery = useInfinitePagination({
    queryKey: QUERY_KEYS.GET_BOOKING_HISTORY_CHANGES_KEY,
    params: bookingHistoryParams,
    enabled: Boolean(bookingId) && isHistoryTabAll,
    initialPage: 1,
    queryFn: bookingsServices.getBookingHistoryChanges,
  });

  const handleBookingAddComment = useCallback(
    (commentData) => {
      addCommentMutation.mutate(
        {
          entityId: bookingId,
          entityType: ENTITY_TYPES_COMMENTS.bookings,
          comment: commentData.content,
        },
        {
          onSuccess: () => {
            commentsResponse.refetch();
          },
          onError: (error) => {
            showError(error);
          },
        }
      );
    },
    [bookingId, addCommentMutation, commentsResponse]
  );

  const handleBookingDeleteComment = useCallback(
    (commentItem) => {
      const commentId = commentItem?.id || commentItem?.resourceId;
      if (!commentId) return;

      deleteCommentMutation.mutate(commentId, {
        onSuccess: () => {
          commentsResponse.refetch();
          if (isHistoryTabAll) bookingHistoryQueryInfiniteQuery.refetch();
        },
        onError: (error) => {
          showError(error);
        },
      });
    },
    [
      commentsResponse,
      deleteCommentMutation,
      bookingHistoryQueryInfiniteQuery,
      isHistoryTabAll,
    ]
  );

  const handleSendMailBooking = useCallback(
    (payload) => {
      const formData = new FormData();
      formData.append('from', payload.from);
      formData.append('body', payload.body);
      formData.append('subject', payload.subject);
      formData.append('mailboxId', payload.mailboxId);
      formData.append('entityId', entityID);
      formData.append('entityType', 'booking');
      formData.append('id', payload.id);

      formData.append('cc', JSON.stringify(payload.cc));

      if (payload.bcc && payload.bcc.length > 0) {
        formData.append('bcc', JSON.stringify(payload.bcc));
      }

      if (payload.to && payload.to.length > 0) {
        formData.append('to', JSON.stringify(payload.to));
      }

      if (payload.attachments && payload.attachments.length > 0) {
        payload.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }
      // send existing attachment ids from the template email (if any)
      if (payload.attachmentIds && payload.attachmentIds.length > 0) {
        formData.append('attachmentIds', JSON.stringify(payload.attachmentIds));
      }

      if (payload.templateId) {
        formData.append('templateId', payload.templateId);
      }
      if (payload.templateLang) {
        formData.append('templateLang', payload.templateLang);
      }

      return emailMutation.mutateAsync(formData, {
        onSuccess: () => {
          showSuccess('Email sent successfully!');
          emailsResponse.refetch();
          if (isHistoryTabAll) bookingHistoryQueryInfiniteQuery.refetch();
        },
        onError: (error) => {
          showError(error);
        },
      });
    },
    [
      entityID,
      emailMutation,
      emailsResponse,
      isHistoryTabAll,
      bookingHistoryQueryInfiniteQuery,
    ]
  );

  const handleCreateBookingLabel = useCallback(
    async (labelName) => {
      try {
        const response = await createBookingLabelMutation.mutateAsync(
          labelName
        );

        showSuccess('Label created successfully!');
        return response;
      } catch (error) {
        showError(error);
        throw error;
      }
    },
    [createBookingLabelMutation]
  );

  const handlePatchBookingLabel = useCallback(
    async (labelId, payload) => {
      try {
        const response = await patchBookingLabelMutation.mutateAsync({
          labelId,
          payload,
        });
        labelsResponse.refetch();
        showSuccess('Label updated successfully!');
        return response;
      } catch (error) {
        showError(error);
        throw error;
      }
    },
    [patchBookingLabelMutation, labelsResponse]
  );

  const invalidateBookingHistory = useCallback(() => {
    queryClient.invalidateQueries([QUERY_KEYS.GET_BOOKING_HISTORY_CHANGES_KEY]);
    queryClient.invalidateQueries([
      QUERY_KEYS.GET_BOOKING_CHANGELOG_KEY,
      bookingId,
    ]);
    queryClient.invalidateQueries([
      QUERY_KEYS.GET_BOOKING_COMMENTS_KEY,
      bookingId,
    ]);
  }, [queryClient, bookingId]);

  const normalizedChangelog = useMemo(
    () =>
      handleChangeLog({
        changelog: changelogResponse?.data?.data || [],
        getAssigneeName,
      }),
    [changelogResponse?.data?.data, getAssigneeName]
  );

  const normalizedSMS = useMemo(
    () =>
      handleSMS({
        sms: smsResponse?.data?.data || [],
        getAssigneeName,
      }),
    [smsResponse?.data?.data, getAssigneeName]
  );

  const { remove: removeBookingHistory } = bookingHistoryQueryInfiniteQuery;

  const normalizedEmails = useMemo(
    () =>
      handleEmails({
        emails: emailsResponse?.data?.data || [],
        getAssigneeName,
      }),
    [emailsResponse?.data?.data, getAssigneeName]
  );

  useEffect(() => {
    if (isHistoryTabAll) return;

    removeBookingHistory();
  }, [isHistoryTabAll, removeBookingHistory]);

  const bookingHistoryQueryItemsHandled = useMemo(() => {
    return handleInfiniteQueryBookingData({
      data: bookingHistoryQueryInfiniteQuery?.items,
      loggedInUser,
      handleChangelogItem: handleChangelogItemFieldName,
      isSystemActor,
      getAssigneeName,
    });
  }, [
    bookingHistoryQueryInfiniteQuery?.items,
    loggedInUser,
    handleChangelogItemFieldName,
    isSystemActor,
    getAssigneeName,
  ]);

  const bookingHistoryQueryData = useMemo(() => {
    if (isHistoryTabComment) {
      return {
        ...commentsResponse,
        items: comments,
      };
    }
    if (isHistoryTabChangelog) {
      return {
        ...changelogResponse,
        items: normalizedChangelog,
      };
    }
    if (isHistoryTabSMS) {
      return {
        ...smsResponse,
        items: normalizedSMS,
      };
    }
    if (isHistoryTabEmails) {
      return {
        ...emailsResponse,
        items: normalizedEmails,
      };
    }
    return {
      ...bookingHistoryQueryInfiniteQuery,
      items: bookingHistoryQueryItemsHandled,
    };
  }, [
    isHistoryTabComment,
    isHistoryTabChangelog,
    commentsResponse,
    changelogResponse,
    comments,
    normalizedChangelog,
    isHistoryTabSMS,
    smsResponse,
    normalizedSMS,
    isHistoryTabEmails,
    emailsResponse,
    normalizedEmails,
    bookingHistoryQueryInfiniteQuery,
    bookingHistoryQueryItemsHandled,
  ]);
  const bookingHistoryQuery = useMemo(() => {
    return {
      ...bookingHistoryQueryData,
      onAddComment: handleBookingAddComment,
      onDeleteComment: handleBookingDeleteComment,
      onSendMail: handleSendMailBooking,
      selectedMailboxId: selectedMailbox?.id,
      selectedMailboxAddress: selectedMailbox?.address,
      agent,
      initialReceivers: user.email ? [user.email] : [],
    };
  }, [
    agent,
    user,
    selectedMailbox,
    bookingHistoryQueryData,
    handleBookingAddComment,
    handleBookingDeleteComment,
    handleSendMailBooking,
  ]);
  const loading =
    bookingResponse?.isLoading ||
    userResponse?.isLoading ||
    isAssigneesLoading ||
    enumsResponse?.isLoading;

  useBookingErrors({
    ordersResponse,
    bookingResponse,
    userResponse,
    commentsResponse,
    changelogResponse,
    smsResponse,
    emailsResponse,
    bookingHistoryQueryInfiniteQuery,
    boardsEnumsResponse,
    linkedTasksResponse,
  });

  return {
    agent,
    loading,
    bookingId,
    isBookingLost,
    booking,
    stages,
    board: 'bookings',
    user,
    vehicle,
    loggedInUser,
    assignees,
    orders,
    bookingHistoryQuery,
    customerTypesOptions,
    lostReasonOptions,
    bookingLabelsOptions,
    linkedTasks,
    getBooking: bookingResponse.refetch,
    getUser: userResponse.refetch,
    getChangeLog: changelogResponse.refetch,
    getComments: commentsResponse.refetch,
    getBookingLabels: labelsResponse.refetch,
    updateBooking,
    handleCreateBookingLabel,
    handlePatchBookingLabel,
    invalidateBookingHistory,
  };
};
